import { oc } from "@orpc/contract";
import { z } from "zod";
import { ComputerFileEntrySchema } from "./computer-files.js";

const id = z.string().min(1).max(128);
const target = z.object({ computerId: id });
const controlled = target.extend({ leaseId: id });
/**
 * A seat that runs on the person's own computer. Its screen, keyboard and pointer are native and
 * never travel through the service, so the service holds its identity and state but cannot open,
 * control or power it.
 */
export const LOCAL_ATLANTIS_KIND = "local-atlantis";
/** `installId` is the desktop app's stable per-machine identity for its local seat. */
const installId = z.string().regex(/^[a-f0-9-]{36}$/);
const localState = z.enum(["stopped", "booting", "running", "suspending", "error"]);

export const HumanComputerStatusSchema = z.object({
  id,
  kind: z.string(),
  state: z.string(),
  leaseId: id.nullable(),
  expiresAt: z.string().nullable(),
  powerSupported: z.boolean().default(false),
  /** Whether stopping this computer backs its home up first. */
  backsUp: z.boolean().default(false),
  /** Set when the last stop could not back the home up and left the computer running. */
  backupFailedAt: z.string().nullable().default(null),
  /** True for a seat installed on the person's own computer; the service cannot open its screen. */
  local: z.boolean().default(false),
  /**
   * Which installation a local seat belongs to, so a client can tell this machine's seat from one
   * on another of the person's computers. Null for a hosted seat.
   */
  installId: z.string().nullable().default(null),
});
export type HumanComputerStatus = z.infer<typeof HumanComputerStatusSchema>;

/** The host that runs a local seat reports its state; nothing in the service can change it. */
export const localHumanComputerContract = {
  /** Idempotent for one machine: the same installation keeps the same computer. */
  registerLocal: oc
    .input(z.object({ installId, name: z.string().trim().min(1).max(80).optional() }))
    .output(HumanComputerStatusSchema),
  reportLocal: oc
    .input(target.extend({ installId, state: localState }))
    .output(HumanComputerStatusSchema),
  /** Removes the service's record of this machine's seat. Its local files are untouched. */
  forgetLocal: oc.input(target.extend({ installId })).output(z.object({ ok: z.literal(true) })),
};
export const humanComputerContract = {
  ...localHumanComputerContract,
  list: oc.output(z.array(HumanComputerStatusSchema)),
  status: oc.input(target).output(HumanComputerStatusSchema),
  power: oc
    // Stopping backs the home up first; the owner can stop without one after that fails.
    .input(
      target.extend({ state: z.enum(["running", "stopped"]), backUp: z.boolean().default(true) }),
    )
    .output(HumanComputerStatusSchema),
  connect: oc
    .input(target)
    .output(z.object({ url: z.string(), leaseId: id, expiresAt: z.string() })),
  screenUrl: oc
    .input(target.extend({ leaseId: id.optional() }))
    .output(z.object({ url: z.string() })),
  terminalUrl: oc.input(controlled).output(z.object({ url: z.string() })),
  renew: oc.input(controlled).output(z.object({ leaseId: id, expiresAt: z.string() })),
  release: oc.input(controlled).output(z.object({ ok: z.literal(true) })),
  files: oc
    .input(target.extend({ path: z.string().max(4096).default("") }))
    .output(z.array(ComputerFileEntrySchema)),
  readFile: oc
    .input(target.extend({ path: z.string().min(1).max(4096) }))
    .output(z.object({ contentBase64: z.string() })),
  writeFile: oc
    .input(
      controlled.extend({
        path: z.string().min(1).max(4096),
        contentBase64: z.string().max(12 * 1024 * 1024),
      }),
    )
    .output(z.object({ saved: z.literal(true) })),
};
